import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Download } from 'lucide-react';
import { API } from '../config';
import { exportToPDF, exportToExcel } from '../utils/exportUtils';
import { MonthlyBarChart } from './MonthlyBarChart';

const SummaryDrillDownModal = ({ isOpen, onClose, plantId, plantName, year, summaryType, summaryLabel, token }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const loadSummaryData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/summary/${plantId}/${year}/${summaryType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [plantId, year, summaryType, token]);

  useEffect(() => {
    if (isOpen && plantId && summaryType) {
      loadSummaryData();
    }
  }, [isOpen, plantId, year, summaryType, loadSummaryData]);

  const handleExportPDF = () => {
    if (!data) return;
    const columns = ['Month', 'Days', 'Production (MT)', 'Despatches (MT)', 'Cost (Rs)', 'Rs/MT'];
    const rows = data.monthly_data.map(m => [
      m.month_name, m.days_with_data || '-', m.production_mt || '-',
      m.despatches_mt || '-', m.cost || '-', m.cost_per_mt || '-'
    ]);
    rows.push(['TOTAL/AVG', `${data.summary.months_with_data} months`, data.summary.total_production_mt,
      data.summary.total_despatches_mt, data.summary.total_cost, data.summary.avg_cost_per_mt]);
    exportToPDF(
      `${summaryLabel} - Monthly Breakdown`,
      `${plantName} | Year: ${year}`,
      columns, rows,
      `${summaryType}_breakdown_${plantName}_${year}`
    );
  };

  const handleExportExcel = () => {
    if (!data) return;
    const columns = ['Month', 'Days', 'Production (MT)', 'Despatches (MT)', 'Cost (Rs)', 'Rs/MT'];
    const rows = data.monthly_data.map(m => [
      m.month_name, m.days_with_data || 0, m.production_mt, m.despatches_mt, m.cost, m.cost_per_mt
    ]);
    rows.push(['TOTAL/AVG', data.summary.months_with_data, data.summary.total_production_mt,
      data.summary.total_despatches_mt, data.summary.total_cost, data.summary.avg_cost_per_mt]);
    exportToExcel(
      `${summaryLabel} - ${plantName} - ${year}`,
      columns, rows,
      `${summaryType}_breakdown_${plantName}_${year}`
    );
  };

  if (!isOpen) return null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value);
  };

  const formatValue = (value, type) => {
    if (type === 'cost' || type === 'cost_per_mt') {
      return formatCurrency(value);
    }
    return `${value} MT`;
  };

  const getHeaderColor = (type) => {
    switch(type) {
      case 'production': return 'from-blue-600 to-blue-800';
      case 'despatches': return 'from-green-600 to-green-800';
      case 'cost': return 'from-orange-600 to-orange-800';
      case 'cost_per_mt': return 'from-purple-600 to-purple-800';
      default: return 'from-indigo-600 to-purple-600';
    }
  };

  const CHART_COLOR = {
    production: '#2563EB',
    despatches: '#16A34A',
    cost: '#EA580C',
    cost_per_mt: '#9333EA',
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className={`sticky top-0 bg-gradient-to-r ${getHeaderColor(summaryType)} text-white p-4 sm:p-6 sm:rounded-t-xl z-10`}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h2 className="text-lg sm:text-2xl font-bold truncate">{summaryLabel} - Monthly Breakdown</h2>
              <p className="text-xs sm:text-base text-white/80 mt-0.5 truncate">{plantName} - {year}</p>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {data && (
                <>
                  <button onClick={handleExportPDF}
                    className="bg-white/20 hover:bg-white/30 text-white px-2 sm:px-3 py-2 rounded-lg transition-colors flex items-center text-xs sm:text-sm"
                    data-testid="summary-export-pdf-btn">
                    <Download className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">PDF</span>
                  </button>
                  <button onClick={handleExportExcel}
                    className="bg-white/20 hover:bg-white/30 text-white px-2 sm:px-3 py-2 rounded-lg transition-colors flex items-center text-xs sm:text-sm"
                    data-testid="summary-export-excel-btn">
                    <Download className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Excel</span>
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-colors"
              >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#156ACA]"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Yearly Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border border-blue-200">
                  <p className="text-xs sm:text-sm text-blue-600">Total Production</p>
                  <p className="text-lg sm:text-2xl font-bold text-blue-800 break-words">{data.summary.total_production_mt} MT</p>
                </div>
                <div className="bg-green-50 p-3 sm:p-4 rounded-lg border border-green-200">
                  <p className="text-xs sm:text-sm text-green-600">Total Despatches</p>
                  <p className="text-lg sm:text-2xl font-bold text-green-800 break-words">{data.summary.total_despatches_mt} MT</p>
                </div>
                <div className="bg-orange-50 p-3 sm:p-4 rounded-lg border border-orange-200">
                  <p className="text-xs sm:text-sm text-orange-600">Total Cost</p>
                  <p className="text-lg sm:text-2xl font-bold text-orange-800 break-words">{formatCurrency(data.summary.total_cost)}</p>
                </div>
                <div className="bg-purple-50 p-3 sm:p-4 rounded-lg border border-purple-200">
                  <p className="text-xs sm:text-sm text-purple-600">Avg Cost/MT</p>
                  <p className="text-lg sm:text-2xl font-bold text-purple-800 break-words">{formatCurrency(data.summary.avg_cost_per_mt)}</p>
                </div>
              </div>

              {/* Bar Chart */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly {data.label} Trend</h3>
                <MonthlyBarChart
                  data={data.monthly_data.map((m) => {
                    const valueByType = {
                      production: m.production_mt,
                      despatches: m.despatches_mt,
                      cost: m.cost,
                      cost_per_mt: m.cost_per_mt,
                    };
                    return { month: m.month, month_name: m.month_name, value: valueByType[summaryType] };
                  })}
                  color={CHART_COLOR[summaryType] || '#6366F1'}
                  valueLabel={`${data.label} (${data.unit})`}
                  formatValue={(v) => formatValue(v, summaryType)}
                />
              </div>

              {/* Monthly Table */}
              <div className="bg-white rounded-lg border overflow-hidden">
                <h3 className="text-lg font-semibold text-gray-900 p-4 border-b bg-gray-50">Monthly Details</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Days</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Production (MT)</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Despatches (MT)</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost (₹)</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">₹/MT</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data.monthly_data.map((month) => (
                        <tr key={month.month} className={month.production_mt > 0 ? '' : 'bg-gray-50 text-gray-400'}>
                          <td className="px-4 py-3 font-medium">{month.month_name}</td>
                          <td className="px-4 py-3 text-right">{month.days_with_data || '-'}</td>
                          <td className="px-4 py-3 text-right font-semibold text-blue-600">{month.production_mt || '-'}</td>
                          <td className="px-4 py-3 text-right text-green-600">{month.despatches_mt || '-'}</td>
                          <td className="px-4 py-3 text-right text-orange-600">{month.cost ? formatCurrency(month.cost) : '-'}</td>
                          <td className="px-4 py-3 text-right font-semibold text-purple-600">{month.cost_per_mt ? formatCurrency(month.cost_per_mt) : '-'}</td>
                        </tr>
                      ))}
                      {/* Totals Row */}
                      <tr className="bg-gray-100 font-bold">
                        <td className="px-4 py-3">TOTAL / AVG</td>
                        <td className="px-4 py-3 text-right">{data.summary.months_with_data} months</td>
                        <td className="px-4 py-3 text-right text-blue-700">{data.summary.total_production_mt}</td>
                        <td className="px-4 py-3 text-right text-green-700">{data.summary.total_despatches_mt}</td>
                        <td className="px-4 py-3 text-right text-orange-700">{formatCurrency(data.summary.total_cost)}</td>
                        <td className="px-4 py-3 text-right text-purple-700">{formatCurrency(data.summary.avg_cost_per_mt)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryDrillDownModal;
